import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "../i18n";
import { IMAGES } from "../data/menu";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative h-screen min-h-[640px] overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <motion.img
          src={IMAGES.hero}
          alt="Pizza cotta a legna"
          data-testid="hero-image"
          className="h-full w-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1.02 }}
          transition={{ duration: 2.4, ease: "easeOut" }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center">
        <motion.p
          data-testid="hero-kicker"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="text-xs md:text-sm tracking-[0.35em] uppercase text-sage mb-6"
        >
          {t("hero_kicker")}
        </motion.p>
        <h1 className="font-serif text-cream leading-[0.92] text-[19vw] md:text-[13vw] lg:text-[10.5rem]">
          <Line delay={0.15}>Da</Line>
          <Line delay={0.32}>
            <span className="italic text-sage">Polin</span>
          </Line>
        </h1>
        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-8 max-w-xl text-base md:text-lg text-cream/75 font-light"
        >
          {t("hero_sub")}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/menu"
            data-testid="hero-menu-button"
            className="rounded-full bg-sage px-8 py-4 text-xs tracking-[0.22em] uppercase text-ink font-semibold hover:bg-cream transition-colors duration-300"
          >
            {t("hero_cta_menu")}
          </Link>
          <Link
            to="/gelateria"
            data-testid="hero-gelato-button"
            className="rounded-full border border-cream/40 px-8 py-4 text-xs tracking-[0.22em] uppercase text-cream hover:border-sage hover:text-sage transition-colors duration-300"
          >
            {t("hero_cta_gelato")}
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase text-cream/50">{t("hero_scroll")}</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="block w-px h-10 bg-gradient-to-b from-sage to-transparent"
        />
      </motion.div>
    </section>
  );
};
